import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container, Box, Typography, Avatar, Chip, Divider,
  CircularProgress, Alert, IconButton, Paper, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MessageIcon from "@mui/icons-material/Message";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import { companies as companiesApi, inquiries } from "../api/endpoints";
import type { Company, CompanyType } from "../types";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { NAVY, CRIMSON, CARD_TINT_GRADIENT } from "../theme/theme";
import { alpha } from "@mui/material/styles";

const COMPANY_TYPE_KEYS: Record<CompanyType, string> = {
  "Grocery and Convenience Store": "companyTypes.groceryAndConvenienceStore",
  "Information Technology":        "companyTypes.informationTechnology",
  "Food and Restaurant":           "companyTypes.foodAndRestaurant",
  "Personal Services":             "companyTypes.personalServices",
  Business:                        "companyTypes.business",
  Engineering:                     "companyTypes.engineering",
  Education:                       "companyTypes.education",
  "Finance and Insurance":         "companyTypes.financeAndInsurance",
  "Health Services":               "companyTypes.healthServices",
  "Hotels and Motels":             "companyTypes.hotelsAndMotels",
  "Motor Vehicle":                 "companyTypes.motorVehicle",
  "Agriculture and Wildlife":      "companyTypes.agricultureAndWildlife",
  Entertainment:                   "companyTypes.entertainment",
  "Real Estate and Housing":       "companyTypes.realEstateAndHousing",
  "Safety, Security and Legal":    "companyTypes.safetySecurityAndLegal",
  Transportation:                  "companyTypes.transportation",
  Other:                           "companyTypes.other",
};

export default function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  const [inquireOpen, setInquireOpen] = useState(false);
  const [inquireMsg, setInquireMsg] = useState("");
  const [inquireLoading, setInquireLoading] = useState(false);
  const [inquireError, setInquireError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    companiesApi.get(Number(id))
      .then((co) => {
        setCompany(co ?? null);
        setLikeCount(co?.likes_count ?? 0);
      })
      .catch((err) => setError(err instanceof Error ? err.message : t("business.loadError")))
      .finally(() => setLoading(false));
  }, [id]);

  const handleLike = async () => {
    if (!company) return;
    try {
      if (liked) {
        await companiesApi.unlike(company.id);
        setLiked(false);
        setLikeCount((n) => Math.max(0, n - 1));
      } else {
        await companiesApi.like(company.id);
        setLiked(true);
        setLikeCount((n) => n + 1);
      }
    } catch { /* ignore */ }
  };

  const handleInquire = async () => {
    if (!company || !user || !inquireMsg.trim()) return;
    setInquireLoading(true);
    try {
      await inquiries.create({
        receiver_id: company.owner_id,
        entity_type: "company",
        entity_id: company.id,
        message: inquireMsg.trim(),
      });
      setInquireOpen(false);
      setInquireMsg("");
    } catch (err) {
      setInquireError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setInquireLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Alert severity="error">{error ?? t("business.notFound")}</Alert>
      </Container>
    );
  }

  return (
    <>
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5, py: 1.5,
        overflow: "hidden",
        color: "white",
        background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a7a 55%, ${alpha(CRIMSON, 0.92)} 100%)`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: -40, right: -20,
          width: 130, height: 130,
          borderRadius: "50%",
          background: alpha("#E8A020", 0.18),
          filter: "blur(30px)",
          pointerEvents: "none",
        },
      }}
    >
      <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: "white", position: "relative", zIndex: 1 }}>
        <ArrowBackIcon />
      </IconButton>
      <Typography variant="subtitle1" fontWeight={700} sx={{ position: "relative", zIndex: 1 }}>
        {t("common.business")}
      </Typography>
    </Box>
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ border: `1px solid ${alpha(NAVY, 0.1)}`, borderRadius: 3, p: 3, mb: 2, backgroundImage: CARD_TINT_GRADIENT, backgroundColor: "#FFFFFF" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              background: "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)",
              width: 60,
              height: 60,
              borderRadius: 3,
            }}
          >
            <BusinessIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2} mb={0.5}>
              {company.name}
            </Typography>
            <Chip
              label={t(COMPANY_TYPE_KEYS[company.company_type as CompanyType] ?? "companyTypes.other")}
              size="small"
              sx={{ fontSize: "0.65rem", background: "linear-gradient(135deg, #E8A020 0%, #B9781A 100%)", color: "white", fontWeight: 700 }}
            />
          </Box>
        </Box>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
          {company.description}
        </Typography>

        {/* Like & inquire row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            onClick={user ? handleLike : undefined}
            sx={{
              display: "flex", alignItems: "center", gap: 0.6,
              px: 1.4, py: 0.5, borderRadius: 20,
              cursor: user ? "pointer" : "default",
              border: `1.5px solid ${liked ? "transparent" : alpha(CRIMSON, 0.3)}`,
              backgroundImage: liked ? "linear-gradient(135deg, #DC2626 0%, #B91C2C 100%)" : "none",
              bgcolor: liked ? "transparent" : alpha(CRIMSON, 0.06),
              color: liked ? "#fff" : CRIMSON,
              boxShadow: liked ? `0 3px 10px ${alpha(CRIMSON, 0.35)}` : "none",
              transition: "all 0.15s",
              "&:hover": user ? { bgcolor: liked ? undefined : alpha(CRIMSON, 0.12), borderColor: CRIMSON } : {},
            }}
          >
            {liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
            <Typography variant="caption" fontWeight={700}>{likeCount}</Typography>
          </Box>
          {user && company.owner_id !== user.id && (
            <Box
              onClick={() => { setInquireError(null); setInquireOpen(true); }}
              sx={{
                display: "flex", alignItems: "center", gap: 0.6,
                px: 1.4, py: 0.5, borderRadius: 20,
                cursor: "pointer",
                border: `1.5px solid ${alpha("#B9781A", 0.35)}`,
                bgcolor: alpha("#E8A020", 0.1),
                color: "#8A5D14",
                transition: "all 0.15s",
                "&:hover": {
                  backgroundImage: "linear-gradient(135deg, #E8A020 0%, #B9781A 100%)",
                  bgcolor: "transparent",
                  color: "#fff",
                  borderColor: "transparent",
                  boxShadow: `0 3px 10px ${alpha("#B9781A", 0.35)}`,
                },
              }}
            >
              <MessageIcon sx={{ fontSize: "0.9rem" }} />
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.75rem" }}>
                {t("business.inquire")}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Contact & details */}
      <Paper elevation={0} sx={{ border: `1px solid ${alpha(NAVY, 0.1)}`, borderRadius: 3, p: 2.5, mb: 2, backgroundImage: CARD_TINT_GRADIENT, backgroundColor: "#FFFFFF" }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>{t("business.contactDetails")}</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          {company.phone_number && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PhoneIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="body2">{company.phone_number}</Typography>
            </Box>
          )}
          {company.email && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="body2">{company.email}</Typography>
            </Box>
          )}
          {company.location && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LocationOnIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="body2">
                {[
                  company.location.address_line1,
                  company.location.city,
                  company.location.state_province,
                  company.location.country_code,
                ].filter(Boolean).join(", ")}
              </Typography>
            </Box>
          )}
          {company.owner && (
            <>
              <Divider />
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
                onClick={() => navigate(`/app/profile/${company.owner!.username}`)}
              >
                <PersonIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2" fontWeight={600} sx={{ "&:hover": { color: CRIMSON } }}>
                  {t("common.owner")}: {company.owner.first_name} {company.owner.last_name}
                </Typography>
              </Box>
            </>
          )}
          <Divider />
          <Typography variant="caption" color="text.secondary">
            {t("business.listed")}: {new Date(company.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      </Paper>
    </Container>

    {/* Inquire Dialog */}
    <Dialog open={inquireOpen} onClose={() => setInquireOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t("business.sendInquiry")} — {company.name}</DialogTitle>
      <DialogContent>
        {inquireError && (
          <Alert severity="error" sx={{ mt: 1 }}>{inquireError}</Alert>
        )}
        <TextField
          label={t("business.inquiryMessage")}
          fullWidth
          margin="normal"
          multiline
          rows={4}
          value={inquireMsg}
          onChange={(e) => setInquireMsg(e.target.value)}
          placeholder={t("business.inquiryPlaceholder")}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setInquireOpen(false)}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          onClick={handleInquire}
          disabled={inquireLoading || !inquireMsg.trim()}
          sx={{
            color: "white",
            "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", bgcolor: "rgba(0,0,0,0.25)" },
          }}
        >
          {inquireLoading ? t("common.sending") : t("common.send")}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
